import { getFileMetadata } from "@transfr-inc/dashboard-components/utils";

import { attributeKey } from "./constants";

// Get the filters data from the data processed
export const getFiltersData = (data) => {
  const simulationsSet = new Set();
  const estimatedTimesSet = new Set();
  const activitiesMap = new Map();
  const industriesSet = new Set();

  const _processLevel = (level) => {
    if (!Array.isArray(level) || level?.length === 0) {
      return [];
    }

    return level
      .map((level) => {
        const items = (level?.items ?? []).map(
          ({
            activity,
            attributes,
            description = "",
            durationMinutes = "",
            id,
            mimeType,
            name,
          }) => {
            const simulations = attributes.find(
              (attribute) => attribute.key === attributeKey.simulationName
            )?.values;
            const industries = attributes.find(
              (attribute) => attribute.key === attributeKey.industry
            )?.values;
            const durationMinutesString = Number.isInteger(durationMinutes)
              ? `${durationMinutes}`
              : undefined;
            const { type } = getFileMetadata(mimeType);

            if (simulations) {
              simulations.forEach((simulation) =>
                simulationsSet.add(simulation)
              );
            }

            if (durationMinutesString) {
              estimatedTimesSet.add(durationMinutesString);
            }

            if (activity) {
              activitiesMap.set(activity.id, activity);
            }

            if (industries) {
              industries.forEach((industry) => industriesSet.add(industry));
            }

            return {
              name,
              id,
              activityName: activity?.name,
              description,
              simulations,
              durationMinutes: durationMinutesString,
              mimeType,
              type,
              industries,
            };
          }
        );
        const childrenItems = _processLevel(level?.children);
        return [items, childrenItems];
      })
      .flat(Infinity);
  };

  const documents = _processLevel(data);

  return {
    activities: Array.from(activitiesMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    ),
    documents,
    simulations: Array.from(simulationsSet).sort(),
    estimatedTimes: Array.from(estimatedTimesSet).sort(),
    industries: Array.from(industriesSet).sort(),
  };
};

export const filterDocuments = (data, filters) => {
  // Convert the estimated times to numbers
  const estimatedTimeNumber = Number(filters?.estimatedTime);

  const _validateItem = (item, filters) => {
    if (filters?.resourceIds && !filters?.resourceIds.includes(item.id)) {
      return false;
    }

    if (filters?.activity?.id && item.activity?.id !== filters?.activity?.id) {
      return false;
    }

    if (filters?.simulation) {
      const itemSimulations =
        item.attributes.find(
          (attribute) => attribute.key === attributeKey.simulationName
        )?.values ?? [];
      if (!itemSimulations.includes(filters?.simulation)) {
        return false;
      }
    }

    if (
      filters?.estimatedTime &&
      item.durationMinutes !== filters?.estimatedTime
    ) {
      return false;
    }

    if (filters?.industry) {
      const itemIndustries =
        item.attributes.find(
          (attribute) => attribute.key === attributeKey.industry
        )?.values ?? [];

      if (!itemIndustries.includes(filters?.industry)) {
        return false;
      }
    }

    return true;
  };

  const _filter = (data, filters) => {
    if (!Array.isArray(data) || data?.length === 0) {
      return [];
    }

    // If no filters are applied, return the data
    if (Object.values(filters).every((value) => !value)) {
      return data;
    }

    return data
      .map((level) => {
        const result = { ...level };

        result.items = level.items.filter((item) =>
          _validateItem(item, filters)
        );

        if (level.children?.length > 0) {
          result.children = _filter(level.children, filters).filter(Boolean);
        }

        if (result.items.length > 0 || result.children?.length > 0) {
          result.isOpen = true;
          return result;
        }

        return undefined;
      })
      .filter(Boolean);
  };

  return _filter([...data], {
    ...filters,
    estimatedTime: estimatedTimeNumber,
  });
};

export const getVisibleItemIds = (node) => {
  const ids = (node.items ?? []).map((item) => item.id);
  (node.children ?? []).forEach((child) => ids.push(...getVisibleItemIds(child)));
  return ids;
};

export const getVisibleItemIdsFromData = (data) =>
  (data ?? []).flatMap((node) => getVisibleItemIds(node));
